import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Eye,
  Flag,
  Search,
  Filter,
  RefreshCw,
  Plus,
  User,
  MessageSquare,
  Activity,  Ban,
  Check,
  X,
  AlertCircle,
  ChevronDown,
  Zap
} from 'lucide-react';

import CredibilityChip from '../../../components/Credibility/CredibilityChip';

// Import your thunks - adjust paths as needed
import {
  fetchAllModerationEventsThunk,
  fetchGroupModerationEventsThunk,
  createManualModerationEventThunk,
  analyzeAndFlagThunk,
  reanalyzePostThunk,
  updateModerationStatusThunk,
} from '../../../redux/admin/adminModerationActionThunks';

const AdminModerationComponent = ({ darkMode = true }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('events');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(null);
  const [error, setError] = useState(null);

  // Redux selectors
  const events = useSelector(state => state.moderation?.events || []);
  const groupEvents = useSelector(state => state.moderation?.groupEvents || []);
  const postEvents = useSelector(state => state.moderation?.postEvents || {});
  const loading = useSelector(state => state.moderation?.loading || false);
  const moderationError = useSelector(state => state.moderation?.error);
  const lastAction = useSelector(state => state.moderation?.lastAction);

  // Theme classes
  const themeClasses = {
    bg: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    borderLight: darkMode ? 'border-gray-600' : 'border-gray-300',
    hoverBg: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    inputBg: darkMode ? 'bg-gray-700' : 'bg-white',
    inputBorder: darkMode ? 'border-gray-600' : 'border-gray-300',
    buttonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
    buttonSecondary: darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
    buttonDanger: 'bg-red-600 hover:bg-red-700 text-white',
    buttonSuccess: 'bg-green-600 hover:bg-green-700 text-white',
    tableBg: darkMode ? 'bg-gray-800' : 'bg-white',
    tableHeaderBg: darkMode ? 'bg-gray-700' : 'bg-gray-50',
    tableRowHover: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
  };

  // Server-side filter params -- "all" (the UI sentinel for "no filter") is
  // never sent; the backend treats an omitted param as unfiltered (see #12).
  const activeFilterParams = useMemo(() => ({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    reason: reasonFilter !== 'all' ? reasonFilter : undefined,
    search: searchTerm || undefined,
  }), [statusFilter, typeFilter, reasonFilter, searchTerm]);

  // Debounce free-text search so it doesn't refetch on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Any filter change re-queries the backend across the WHOLE table (not
  // just the currently-loaded page) -- this is the real fix for #12:
  // filters used to only ever narrow down whatever 50/100 rows happened to
  // already be in Redux, so "no events found" could be wrong if a matching
  // event just wasn't on the loaded page. A filter change also jumps back
  // to page 1; that reset and the resulting fetch are done in the same
  // effect (rather than two separate effects reacting to the same filter
  // deps) so a filter change while on page 3+ doesn't fire one fetch for
  // the stale page followed immediately by a second corrected one.
  const filterSignature = `${activeFilterParams.status}|${activeFilterParams.type}|${activeFilterParams.reason}|${debouncedSearch}|${scopeFilter}`;
  const prevFilterSignatureRef = useRef(filterSignature);

  // Re-fetch with today's current filters/scope/page -- used by the effect
  // below and by the manual Refresh button, so Refresh can't silently go
  // stale relative to whatever filter is actually active (it previously
  // always re-fetched an unfiltered, group-posts-less, page-1 view
  // regardless of what was on screen).
  const fetchEventsForCurrentView = useCallback(async (page) => {
    const skip = (page - 1) * pageSize;
    const params = { skip, limit: pageSize, ...activeFilterParams, search: debouncedSearch || undefined };
    try {
      setError(null);
      const calls = [];
      // Scope filter, when set to a single scope, skips the request for
      // the other table entirely -- both fewer requests and avoids a
      // stale/irrelevant result set lingering in the other array.
      if (scopeFilter !== 'group_post') calls.push(dispatch(fetchAllModerationEventsThunk(params)));
      if (scopeFilter !== 'post') calls.push(dispatch(fetchGroupModerationEventsThunk(params)));
      await Promise.all(calls);
    } catch (err) {
      setError(t('admin.moderation.errors.loadFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageSize, activeFilterParams.status, activeFilterParams.type, activeFilterParams.reason, debouncedSearch, scopeFilter, t]);

  useEffect(() => {
    const filtersChanged = prevFilterSignatureRef.current !== filterSignature;
    prevFilterSignatureRef.current = filterSignature;
    if (filtersChanged && currentPage !== 1) {
      setCurrentPage(1);
      return; // the setCurrentPage above re-triggers this effect with page already 1
    }
    fetchEventsForCurrentView(filtersChanged ? 1 : currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterSignature, fetchEventsForCurrentView]);

  // Regular-post events (target: "post") and group-post events (target:
  // "group_post") come from separate tables/endpoints -- normalize
  // group_post_id -> post_id so the existing "Post #{id}" render/search
  // code works unmodified for both, and keep `target` as the scope badge.
  const allEvents = useMemo(() => {
    const normalizedPost = events.map(e => ({ ...e, target: e.target || 'post' }));
    const normalizedGroup = groupEvents.map(e => ({ ...e, post_id: e.group_post_id, target: 'group_post' }));
    return [...normalizedPost, ...normalizedGroup];
  }, [events, groupEvents]);

  // status/type/reason/search are now applied server-side (see #12 -- the
  // fetch effect above re-queries the whole table on every filter change),
  // so allEvents is already the correctly-filtered set. Scope is the one
  // filter still applied here: when scopeFilter === 'all' both tables were
  // fetched and nothing further needs to happen; when it's a single scope,
  // this is a no-op safety net against the other (unfetched, stale) array.
  const filteredEvents = useMemo(() => {
    if (scopeFilter === 'all') return allEvents;
    return allEvents.filter(event => event.target === scopeFilter);
  }, [allEvents, scopeFilter]);

  // The status-update endpoint is now scope-aware (PATCH /admin/moderation/{scope}/{event_id}/status),
  // so both post and group-post rows are selectable/actionable -- selection is
  // keyed by "target-id" (see handleBulkAction) rather than bare id, since
  // moderation_events/group_moderation_events have independent id sequences.
  const selectableEvents = filteredEvents;

  // Fixed, known enum values (matching the backend's ModerationStatus/
  // ModerationType and the reasons offered in the create-event modal below)
  // -- NOT derived from allEvents. Deriving them from loaded data used to
  // mean the dropdown silently lost options as soon as any filter narrowed
  // the loaded set (e.g. filtering to status=removed would hide every type
  // except whatever types the removed events happened to have), which
  // would have made switching filters from an already-filtered state
  // confusing even after the #12 pagination fix.
  const filterOptions = useMemo(() => ({
    statuses: ['open', 'reviewed', 'removed', 'false_positive', 'violation'],
    types: ['auto_flag', 'user_report', 'manual_flag'],
    reasons: ['spam', 'hate_speech', 'harassment', 'inappropriate_content', 'violence', 'other'],
  }), []);

  // "post" | "group_post" (frontend scope tag) -> "post" | "group" (backend path param)
  const toBackendScope = (target) => (target === 'group_post' ? 'group' : 'post');

  // Handle status updates -- now scope-aware, works for both post and group-post rows
  const handleStatusUpdate = useCallback(async (eventId, newStatus, target = 'post') => {
    try {
      setError(null);
      await dispatch(updateModerationStatusThunk({ eventId, new_status: newStatus, scope: toBackendScope(target) }));
    } catch (err) {
      setError(t('admin.moderation.errors.statusUpdateFailed', { message: err.message }));
    }
  }, [dispatch, t]);

  // Handle bulk actions -- selectedEvents now holds "target-id" composite keys
  // (a plain id would be ambiguous: moderation_events and group_moderation_events
  // have independent id sequences and can share the same numeric id).
  const handleBulkAction = useCallback(async (action) => {
    if (!selectedEvents.length) {
      setError(t('admin.moderation.errors.selectEventsFirst'));
      return;
    }

    try {
      setError(null);

      const promises = selectedEvents.map((key) => {
        const [target, idStr] = key.split('-');
        return dispatch(updateModerationStatusThunk({ eventId: Number(idStr), new_status: action, scope: toBackendScope(target) }));
      });

      await Promise.all(promises);
      setSelectedEvents([]);
    } catch (err) {
      setError(t('admin.moderation.errors.bulkActionFailed', { message: err.message }));
    }
  }, [selectedEvents, dispatch, t]);

  // Handle post analysis
  const handleAnalyzePost = useCallback(async (postId, userId, content) => {
    try {
      setError(null);
      const result = await dispatch(analyzeAndFlagThunk({ post_id: postId, user_id: userId, content }));
      
      if (result.payload) {
        setError(null);
      }
    } catch (err) {
      setError(t('admin.moderation.errors.analysisFailed', { message: err.message }));
    }
  }, [dispatch, t]);

  // Handle post reanalysis
  const handleReanalyzePost = useCallback(async (postId) => {
    try {
      setError(null);
      await dispatch(reanalyzePostThunk({ postId }));
    } catch (err) {
      setError(t('admin.moderation.errors.reanalysisFailed', { message: err.message }));
    }
  }, [dispatch, t]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('admin.moderation.notAvailable');
    return new Date(dateString).toLocaleString();
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      reviewed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      removed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      false_positive: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    };
    
    return badges[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  // Get type badge
  const getTypeBadge = (type) => {
    const badges = {
      auto_flag: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      user_report: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      manual_flag: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
    };
    
    return badges[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    if (!severity?.level) return 'text-gray-500';
    
    const colors = {
      low: 'text-green-600 dark:text-green-400',
      medium: 'text-yellow-600 dark:text-yellow-400', 
      high: 'text-orange-600 dark:text-orange-400',
      critical: 'text-red-600 dark:text-red-400'
    };
    
    return colors[severity.level] || 'text-gray-500';
  };

  // Error display component
  const ErrorDisplay = ({ error, onDismiss }) => (
    <div className={`${darkMode ? 'bg-red-900/50 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <AlertTriangle className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-600'} mr-2`} />
          <span className={`${darkMode ? 'text-red-300' : 'text-red-800'} font-medium`}>
            {error}
          </span>
        </div>
        {onDismiss && (
          <button 
            onClick={onDismiss}
            className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-500'}`}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );

  // Statistics cards
  const StatCards = () => {
    const stats = useMemo(() => {
      const total = allEvents.length;
      const open = allEvents.filter(e => e.status === 'open').length;
      const reviewed = allEvents.filter(e => e.status === 'reviewed').length;
      const autoFlags = allEvents.filter(e => e.type === 'auto_flag').length;
      const userReports = allEvents.filter(e => e.type === 'user_report').length;

      return { total, open, reviewed, autoFlags, userReports };
    }, [allEvents]);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.moderation.stats.totalEvents')}</p>
              <p className={`text-2xl font-bold ${themeClasses.text}`}>{stats.total}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Activity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.moderation.stats.openEvents')}</p>
              <p className={`text-2xl font-bold text-yellow-600 dark:text-yellow-400`}>{stats.open}</p>
            </div>
            <div className="h-12 w-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.moderation.stats.reviewed')}</p>
              <p className={`text-2xl font-bold text-green-600 dark:text-green-400`}>{stats.reviewed}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.moderation.stats.autoFlags')}</p>
              <p className={`text-2xl font-bold text-purple-600 dark:text-purple-400`}>{stats.autoFlags}</p>
            </div>
            <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Zap className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{t('admin.moderation.stats.userReports')}</p>
              <p className={`text-2xl font-bold text-orange-600 dark:text-orange-400`}>{stats.userReports}</p>
            </div>
            <div className="h-12 w-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <Flag className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Create manual event modal
  const CreateEventModal = () => {
    const [formData, setFormData] = useState({
      post_id: '',
      user_id: '',
      reason: '',
      type: 'manual_flag',
      status: 'open',
      details: ''
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        setError(null);
        
        const payload = {
          ...formData,
          details: formData.details ? JSON.parse(formData.details) : null
        };
        
        await dispatch(createManualModerationEventThunk(payload));
        setShowCreateModal(false);
        setFormData({
          post_id: '',
          user_id: '',
          reason: '',
          type: 'manual_flag',
          status: 'open',
          details: ''
        });
      } catch (err) {
        setError(t('admin.moderation.errors.createEventFailed', { message: err.message }));
      }
    };

    if (!showCreateModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 w-full max-w-md mx-4 shadow-xl`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.moderation.createModal.title')}</h3>
            <button
              onClick={() => setShowCreateModal(false)}
              className={`${themeClasses.textMuted} hover:${themeClasses.text}`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>
                {t('admin.moderation.createModal.postIdLabel')}
              </label>
              <input
                type="number"
                required
                value={formData.post_id}
                onChange={(e) => setFormData(prev => ({ ...prev, post_id: e.target.value }))}
                className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>
                {t('admin.moderation.createModal.userIdLabel')}
              </label>
              <input
                type="number"
                required
                value={formData.user_id}
                onChange={(e) => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
                className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>
                {t('admin.moderation.createModal.reasonLabel')}
              </label>
              <select
                required
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500`}
              >
                <option value="">{t('admin.moderation.createModal.selectReason')}</option>
                <option value="spam">Spam</option>
                <option value="hate_speech">Hate Speech</option>
                <option value="harassment">Harassment</option>
                <option value="inappropriate_content">Inappropriate Content</option>
                <option value="violence">Violence</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>
                {t('admin.moderation.createModal.detailsLabel')}
              </label>
              <textarea
                rows={3}
                value={formData.details}
                onChange={(e) => setFormData(prev => ({ ...prev, details: e.target.value }))}
                placeholder={t('admin.moderation.createModal.detailsPlaceholder')}
                className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className={`px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
              >
                {t('admin.moderation.createModal.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 ${themeClasses.buttonPrimary} rounded-lg transition-colors disabled:opacity-50`}
              >
                {t('admin.moderation.createModal.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Post analysis modal
  const PostAnalysisModal = () => {
    const [analysisData, setAnalysisData] = useState({
      post_id: '',
      user_id: '',
      content: ''
    });

    const handleAnalyze = async (e) => {
      e.preventDefault();
      await handleAnalyzePost(
        parseInt(analysisData.post_id),
        parseInt(analysisData.user_id),
        analysisData.content
      );
      setShowPostModal(null);
      setAnalysisData({ post_id: '', user_id: '', content: '' });
    };

    if (!showPostModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 w-full max-w-md mx-4 shadow-xl`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.moderation.analyzeModal.title')}</h3>
            <button
              onClick={() => setShowPostModal(null)}
              className={`${themeClasses.textMuted} hover:${themeClasses.text}`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleAnalyze} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>
                {t('admin.moderation.analyzeModal.postIdLabel')}
              </label>
              <input
                type="number"
                required
                value={analysisData.post_id}
                onChange={(e) => setAnalysisData(prev => ({ ...prev, post_id: e.target.value }))}
                className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>
                {t('admin.moderation.analyzeModal.userIdLabel')}
              </label>
              <input
                type="number"
                required
                value={analysisData.user_id}
                onChange={(e) => setAnalysisData(prev => ({ ...prev, user_id: e.target.value }))}
                className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>
                {t('admin.moderation.analyzeModal.contentLabel')}
              </label>
              <textarea
                rows={4}
                required
                value={analysisData.content}
                onChange={(e) => setAnalysisData(prev => ({ ...prev, content: e.target.value }))}
                placeholder={t('admin.moderation.analyzeModal.contentPlaceholder')}
                className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowPostModal(null)}
                className={`px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
              >
                {t('admin.moderation.analyzeModal.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 ${themeClasses.buttonPrimary} rounded-lg transition-colors disabled:opacity-50`}
              >
                {t('admin.moderation.analyzeModal.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {(error || moderationError) && (
        <ErrorDisplay 
          error={error || moderationError} 
          onDismiss={() => setError(null)} 
        />
      )}

      {/* Statistics Cards */}
      <StatCards />

      {/* Main Content */}
      <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} shadow-sm`}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <h2 className={`text-xl font-semibold ${themeClasses.text} flex items-center`}>
                <Shield className="h-6 w-6 mr-2" />
                {t('admin.moderation.heading')}
              </h2>
              <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                {t('admin.moderation.eventsCount', { count: filteredEvents.length })}
              </span>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowPostModal('analyze')}
                className={`flex items-center px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
              >
                <Zap className="h-4 w-4 mr-2" />
                {t('admin.moderation.analyzePostButton')}
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className={`flex items-center px-4 py-2 ${themeClasses.buttonPrimary} rounded-lg transition-colors`}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('admin.moderation.createEventButton')}
              </button>
              <button
                onClick={() => fetchEventsForCurrentView(currentPage)}
                disabled={loading}
                className={`flex items-center px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {t('admin.moderation.refresh')}
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textMuted} h-5 w-5`} />
              <input
                type="text"
                placeholder={t('admin.moderation.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
              />
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-4 py-2 border ${themeClasses.borderLight} ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('admin.moderation.filtersButton')}
                <ChevronDown className={`h-4 w-4 ml-2 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>{t('admin.moderation.filterBar.scope')}</label>
                  <select
                    value={scopeFilter}
                    onChange={(e) => setScopeFilter(e.target.value)}
                    className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg`}
                  >
                    <option value="all">{t('admin.moderation.filterBar.allScopes')}</option>
                    <option value="post">{t('admin.moderation.filterBar.posts')}</option>
                    <option value="group_post">{t('admin.moderation.filterBar.groupPosts')}</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>{t('admin.moderation.filterBar.status')}</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg`}
                  >
                    <option value="all">{t('admin.moderation.filterBar.allStatuses')}</option>
                    {filterOptions.statuses.map(status => (
                      <option key={status} value={status}>
                        {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>{t('admin.moderation.filterBar.type')}</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg`}
                  >
                    <option value="all">{t('admin.moderation.filterBar.allTypes')}</option>
                    {filterOptions.types.map(type => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1`}>{t('admin.moderation.filterBar.reason')}</label>
                  <select
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value)}
                    className={`w-full px-3 py-2 border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded-lg`}
                  >
                    <option value="all">{t('admin.moderation.filterBar.allReasons')}</option>
                    {filterOptions.reasons.map(reason => (
                      <option key={reason} value={reason}>
                        {reason.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setTypeFilter('all');
                    setReasonFilter('all');
                    setScopeFilter('all');
                    setSearchTerm('');
                  }}
                  className={`px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
                >
                  {t('admin.moderation.filterBar.clearFilters')}
                </button>
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedEvents.length > 0 && (
            <div className={`mt-4 p-4 ${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'} rounded-lg border`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                  {t('admin.moderation.bulkActions.selectedCount', { count: selectedEvents.length })}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleBulkAction('reviewed')}
                    disabled={loading}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <Check className="h-3 w-3 mr-1 inline" />
                    {t('admin.moderation.bulkActions.markReviewed')}
                  </button>
                  <button
                    onClick={() => handleBulkAction('removed')}
                    disabled={loading}
                    className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    <Ban className="h-3 w-3 mr-1 inline" />
                    {t('admin.moderation.bulkActions.remove')}
                  </button>
                  <button
                    onClick={() => handleBulkAction('false_positive')}
                    disabled={loading}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle className="h-3 w-3 mr-1 inline" />
                    {t('admin.moderation.bulkActions.falsePositive')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Events Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin mr-2 text-blue-600" />
              <span className={themeClasses.text}>{t('admin.moderation.loading')}</span>
            </div>
          ) : filteredEvents.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={themeClasses.tableHeaderBg}>
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectableEvents.length > 0 && selectedEvents.length === selectableEvents.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedEvents(selectableEvents.map(ev => `${ev.target}-${ev.id}`));
                        } else {
                          setSelectedEvents([]);
                        }
                      }}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                    />
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>
                    {t('admin.moderation.table.event')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>
                    {t('admin.moderation.table.scope')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>
                    {t('admin.moderation.table.postUser')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>
                    {t('admin.moderation.table.typeStatus')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>
                    {t('admin.moderation.table.reason')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>
                    {t('admin.moderation.table.severity')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>
                    {t('admin.moderation.table.created')}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>
                    {t('admin.moderation.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className={`${themeClasses.tableBg} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredEvents.map((event) => (
                  <tr key={`${event.target}-${event.id}`} className={themeClasses.tableRowHover}>
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(`${event.target}-${event.id}`)}
                        onChange={(e) => {
                          const key = `${event.target}-${event.id}`;
                          if (e.target.checked) {
                            setSelectedEvents([...selectedEvents, key]);
                          } else {
                            setSelectedEvents(selectedEvents.filter(k => k !== key));
                          }
                        }}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 disabled:opacity-40"
                      />
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-3 ${
                          event.status === 'open' ? 'bg-yellow-500' :
                          event.status === 'reviewed' ? 'bg-blue-500' :
                          event.status === 'removed' ? 'bg-red-500' :
                          event.status === 'false_positive' ? 'bg-green-500' : 'bg-gray-500'
                        }`}></div>
                        <div>
                          <div className={`text-sm font-medium ${themeClasses.text}`}>
                            #{event.id}
                          </div>
                          <div className={`text-xs ${themeClasses.textMuted}`}>
                            {formatDate(event.created_at)}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        event.target === 'group_post'
                          ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {event.target === 'group_post' ? t('admin.moderation.scopeGroupPost') : t('admin.moderation.scopePost')}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className={`text-sm ${themeClasses.text} flex items-center`}>
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {event.target === 'group_post' ? t('admin.moderation.scopeGroupPost') : t('admin.moderation.scopePost')} #{event.post_id}
                        </div>
                        <div className={`text-sm ${themeClasses.textMuted} flex items-center gap-1.5 flex-wrap`}>
                          <User className="h-3 w-3" />
                          {event.author_username ? `@${event.author_username}` : t('admin.moderation.userFallback', { id: event.user_id })}
                          <CredibilityChip cred={event.author_credibility} size="xs" showAccuracy={false} />
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadge(event.type)}`}>
                          {event.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <br />
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(event.status)}`}>
                          {event.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className={`text-sm font-medium ${themeClasses.text}`}>
                        {event.reason?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || t('admin.moderation.notAvailable')}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      {event.details?.severity ? (
                        <div className="space-y-1">
                          <span className={`text-sm font-medium ${getSeverityColor(event.details.severity)}`}>
                            {event.details.severity.level?.toUpperCase() || t('admin.moderation.notAvailable')}
                          </span>
                          {event.details.severity.score && (
                            <div className={`text-xs ${themeClasses.textMuted}`}>
                              {t('admin.moderation.scoreLabel', { score: event.details.severity.score.toFixed(2) })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className={`text-sm ${themeClasses.textMuted}`}>{t('admin.moderation.notAvailable')}</span>
                      )}
                    </td>

                    <td className={`px-6 py-4 text-sm ${themeClasses.textMuted}`}>
                      <div className="space-y-1">
                        <div>{formatDate(event.created_at)}</div>
                        {event.resolved_at && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            {t('admin.moderation.resolvedLabel', { date: formatDate(event.resolved_at) })}
                          </div>
                        )}
                        {event.moderator_id && (
                          <div className="text-xs">
                            {t('admin.moderation.modLabel', { id: event.moderator_id })}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {/* Quick Actions -- the status-update endpoint is now scope-aware
                            (PATCH /admin/moderation/{scope}/{event_id}/status), so these
                            work for group-post rows too. Reanalyze stays post-only: it
                            calls the NLP microservice fresh via a dedicated post-only
                            endpoint with no group-post equivalent. */}
                        {event.status === 'open' && (
                          <>
                            <button
                              onClick={() => handleStatusUpdate(event.id, 'reviewed', event.target)}
                              className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded transition-colors"
                              title={t('admin.moderation.actionsTooltips.markReviewed')}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(event.id, 'removed', event.target)}
                              className="p-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                              title={t('admin.moderation.actionsTooltips.removeContent')}
                            >
                              <Ban className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(event.id, 'false_positive', event.target)}
                              className="p-1 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/20 rounded transition-colors"
                              title={t('admin.moderation.actionsTooltips.markFalsePositive')}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}

                        {/* Reanalyze for auto_flag events -- regular posts only, see comment above */}
                        {event.target !== 'group_post' && event.type === 'auto_flag' && (
                          <button
                            onClick={() => handleReanalyzePost(event.post_id)}
                            className="p-1 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/20 rounded transition-colors"
                            title={t('admin.moderation.actionsTooltips.reanalyzePost')}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        )}

                        {/* View Details */}
                        <div className="relative group">
                          <button className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          {/* Details Tooltip */}
                          {event.details && (
                            <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <div className="space-y-2">
                                {event.details.reasons && (
                                  <div>
                                    <strong>{t('admin.moderation.detailTooltip.reasons')}</strong> {event.details.reasons.join(', ')}
                                  </div>
                                )}
                                {event.details.main_reason && (
                                  <div>
                                    <strong>{t('admin.moderation.detailTooltip.mainReason')}</strong> {event.details.main_reason}
                                  </div>
                                )}
                                {event.details.vader_score !== undefined && (
                                  <div>
                                    <strong>{t('admin.moderation.detailTooltip.sentimentScore')}</strong> {event.details.vader_score.toFixed(3)}
                                  </div>
                                )}
                                {event.details.notes && (
                                  <div>
                                    <strong>{t('admin.moderation.detailTooltip.notes')}</strong> {event.details.notes}
                                  </div>
                                )}
                              </div>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={`text-center py-12 ${themeClasses.textMuted}`}>
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">
                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || reasonFilter !== 'all' || scopeFilter !== 'all'
                  ? t('admin.moderation.emptyState.noMatch')
                  : t('admin.moderation.emptyState.noEvents')
                }
              </p>
              {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || reasonFilter !== 'all' || scopeFilter !== 'all') && (
                <p className="text-sm">{t('admin.moderation.emptyState.tryAdjusting')}</p>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredEvents.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className={`text-sm ${themeClasses.textMuted}`}>
                {t('admin.moderation.pagination.showing', { shown: Math.min(filteredEvents.length, pageSize), total: filteredEvents.length })}
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1 text-sm border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} rounded`}
                >
                  <option value={25}>{t('admin.moderation.pagination.perPage', { count: 25 })}</option>
                  <option value={50}>{t('admin.moderation.pagination.perPage', { count: 50 })}</option>
                  <option value={100}>{t('admin.moderation.pagination.perPage', { count: 100 })}</option>
                </select>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
                >
                  {t('admin.moderation.pagination.previous')}
                </button>
                <span className={`px-3 py-2 text-sm ${themeClasses.text}`}>
                  {t('admin.moderation.pagination.page', { page: currentPage })}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={filteredEvents.length < pageSize}
                  className={`px-3 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
                >
                  {t('admin.moderation.pagination.next')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateEventModal />
      <PostAnalysisModal />
    </div>
  );
};

export default AdminModerationComponent;